import { Cached, Face, Payment } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import firebase from 'firebase/compat/app';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import * as React from 'react';
import { currentAuthAtom } from '../jotai/atoms';
import adminDescriptionUrl from '../resources/admin-description.svg';
import bannerUrl from '../resources/banner.png';
import googleUrl from '../resources/google.svg';
import logoUrl from '../resources/hackforplay-admin.png';
import flex from '../styles/flex.module.scss';
import { Blank } from './Blank';

const styles = {
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    flexWrap: 'wrap'
  },
  left: {
    backgroundColor: '#ffffff'
  },
  google: {
    backgroundColor: '#4a90e2',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '2px',
    boxShadow: 2,
    color: '#ffffff',
    cursor: 'pointer',
    transition: (theme: Theme) => theme.transitions.create('background-color'),
    '&:hover': {
      backgroundColor: '#77ace9'
    },
    '& > img': {
      margin: '-2px'
    },
    '& > span': {
      whiteSpace: 'nowrap',
      marginLeft: '14px',
      marginRight: '20px'
    }
  },
  disabled: {
    cursor: 'progress',
    backgroundColor: '#e3e3e3',
    '&:hover': {
      backgroundColor: '#e3e3e3'
    },
    opacity: 0.8,
    boxShadow: 'none'
  },
  banner: {
    margin: 2
  },
  logo: {
    margin: 2
  },
  right: {
    flexGrow: 3,
    backgroundColor: '#edf0f3',
    minWidth: 600,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > *': {
      width: 600
    }
  },
  description: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
    '& > svg': {
      marginRight: 1
    }
  }
} as const;

const currentAuthLoadableAtom = loadable(currentAuthAtom);

/**
 * ログインしていない場合に見せるページ
 */
export function Landing({}) {
  const userInfoLoadable = useAtomValue(currentAuthLoadableAtom);

  // 管理者をメールで招待したとき、招待時のメールアドレスでログインさせる
  const params = new URLSearchParams(window.location.search);
  const login_hint = params.get('login_hint');

  const signIn = React.useCallback(() => {
    const provider = new firebase.auth.GoogleAuthProvider();
    if (login_hint) {
      provider.setCustomParameters({ login_hint });
    }

    // アカウントが１つしかなくても必ず選択画面を表示させる
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    firebase
      .auth()
      .signInWithPopup(provider)
      .catch(error => alert(error.message));
  }, []);

  return (
    <Box sx={styles.root}>
      <Box className={classNames(flex.blank, flex.vertical)} sx={styles.left}>
        <Blank grow={2} />
        <Box component="img" src={bannerUrl} alt="" sx={styles.banner} />
        <Box
          component="img"
          src={logoUrl}
          alt="HackforPlay Admin"
          sx={styles.logo}
        />
        <Typography variant="body1" color="text.secondary">
          ハックフォープレイの法人向けサービス
        </Typography>
        <Blank grow={1} />
        <Box
          sx={[
            styles.google,
            userInfoLoadable.state === 'loading' ? styles.disabled : {},
            userInfoLoadable.state === 'hasData' && userInfoLoadable.data
              ? styles.disabled
              : {}
          ]}
          onClick={signIn}
          data-testid="google-signin"
        >
          <img src={googleUrl} alt="" />
          <span>Google でログイン/登録</span>
        </Box>
        <Typography color="text.secondary" align="center">
          <br />
          初めての方・ご利用に関するお問い合わせを
          <br />
          ご希望の方は、最初にご登録をお願いいたします
        </Typography>
        <Blank grow={3} />
      </Box>
      <Box sx={styles.right}>
        <Blank grow={3} />
        <img src={adminDescriptionUrl} alt="" />
        <Blank grow={1} />
        <div>
          <Typography
            variant="subtitle2"
            sx={styles.description}
            color="text.secondary"
          >
            <Face />
            メンバーを作成（ID, パスワード発行）
          </Typography>
          <Typography
            variant="subtitle2"
            sx={styles.description}
            color="text.secondary"
          >
            <Payment />
            複数人の料金をまとめてお支払い
          </Typography>
          <Typography
            variant="subtitle2"
            sx={styles.description}
            color="text.secondary"
          >
            <Cached />
            有料プランの特典を受けられるメンバーを選択可能
          </Typography>
        </div>
        <Blank grow={3} />
      </Box>
    </Box>
  );
}
